import { Button } from "@/components/ui/button";
import { Logo } from "@/components/ui/Logo";
import { Link } from "react-router-dom";
import { Footer } from "@/components/layout/Footer";
import { MetaTags } from "@/components/seo/MetaTags";
import { JsonLd, organizationSchema } from "@/components/seo/JsonLd";
import { ArrowRight, Target, Users, Zap } from "lucide-react";
import { motion } from "framer-motion";

const About = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags 
        title="About Ritual - Agent-Native Operations Platform"
        description="Ritual transforms how teams execute operations. We believe the best work happens when process becomes invisible."
      />
      <JsonLd data={organizationSchema} />
      
      <nav className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container flex h-14 sm:h-16 items-center justify-between px-4 sm:px-6">
          <Link to="/">
            <Logo size="sm" />
          </Link>
          <Link to="/auth">
            <Button size="sm" variant="hero">
              Get Started
              <ArrowRight className="ml-1 h-3 w-3" />
            </Button>
          </Link>
        </div>
      </nav>

      <main className="flex-1">
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-transparent">
          <div className="container px-4 sm:px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
                Operations should feel like{" "}
                <span className="text-gradient-primary">breathing</span>
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto">
                We're building the operating system for modern teams. Where process becomes invisible 
                and execution becomes instinctive.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Mission */}
        <section className="py-16 sm:py-24">
          <div className="container px-4 sm:px-6">
            <div className="max-w-3xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-2xl sm:text-3xl font-bold mb-6">Our Mission</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  Every business runs on processes. Most of those processes live in someone's head, 
                  a dusty document, or an endless WhatsApp thread. When that person leaves, takes a 
                  vacation, or simply forgets—the process breaks.
                </p>
                <p className="text-lg text-muted-foreground mb-6">
                  Ritual exists to solve this. We transform your standard operating procedures from 
                  static documents into living, breathing workflows that guide your team through 
                  execution—one swipe at a time.
                </p>
                <p className="text-lg text-muted-foreground">
                  No more "did you do it?" No more Sunday night anxiety checks. Just proof-backed 
                  completion that lets you sleep soundly.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-16 sm:py-24 bg-muted/30">
          <div className="container px-4 sm:px-6">
            <h2 className="text-2xl sm:text-3xl font-bold mb-12 text-center">What We Believe</h2>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[
                {
                  icon: Target,
                  title: "Simplicity Wins",
                  description: "The best tools disappear. If you have to think about the tool, it's not working. Swipe right. Move on."
                },
                {
                  icon: Users,
                  title: "Teams Over Tools",
                  description: "Technology should amplify human capability, not replace human judgment. We build for the doers."
                },
                {
                  icon: Zap,
                  title: "Speed is Trust",
                  description: "When something takes 2 seconds instead of 2 minutes, people actually do it. Consistency creates reliability."
                }
              ].map((value, index) => {
                const Icon = value.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="text-center"
                  >
                    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary/10 mb-4">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{value.title}</h3>
                    <p className="text-muted-foreground">{value.description}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-24">
          <div className="container px-4 sm:px-6 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-2xl sm:text-3xl font-bold mb-6">
                Ready to make operations invisible?
              </h2>
              <Link to="/auth">
                <Button size="xl" variant="hero">
                  Start Your First Ritual
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default About;
